import React from 'react';
import {
  GridActionsCellItem,
  GridColDef,
  GridRowModel,
  GridRowModes,
  GridRowModesModel,
} from '@mui/x-data-grid';

import SaveIcon from '@mui/icons-material/Save';
import EditIcon from '@mui/icons-material/Edit';
import CancelIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';

import { FDataGrid } from '../grid';
import { makeTempRowId } from '../grid/FGridUtils';

type Props = {
  rows: any[];
  loading?: boolean;
  onChange: (rows: any[]) => void;
  onSubmit: (rows: any[]) => Promise<void>;
  onTaskSelect: (row: any | null) => void;
  selectedTaskId?: any;
};

type State = {
  rowModesModel: GridRowModesModel;
  saving: boolean;
};

class TaskGridPanel extends React.Component<Props, State> {
  state: State = {
    rowModesModel: {},
    saving: false,
  };

  getRowId = (row: any) => row.id || row.rid || row.taskid;

  addRow = () => {
    const id = makeTempRowId('new-task');

    const newRow = {
      id,
      rid: null,
      taskid: null,
      code: '',
      name: '',
      weight: 0,
      description: '',
      status: 1,
      _state: 'new',
    };

    this.props.onChange([...(this.props.rows || []), newRow]);

    setTimeout(() => {
      this.setState({
        rowModesModel: {
          ...this.state.rowModesModel,
          [id]: { mode: GridRowModes.Edit },
        },
      });
    }, 0);
  };

  deleteRow = (id: any) => {
    const rows = (this.props.rows || [])
      .map((r: any) => {
        const key = this.getRowId(r);
        if (String(key) !== String(id)) return r;

        if (String(r.id || '').startsWith('new-')) {
          return { ...r, _state: 'discard' };
        }

        return { ...r, _state: 'deleted', status: 0 };
      })
      .filter((r: any) => r._state !== 'discard');

    this.props.onChange(rows);

    if (String(this.props.selectedTaskId || '') === String(id)) {
      this.props.onTaskSelect(null);
    }
  };

  handleEditClick = (id: any) => {
    this.setState({
      rowModesModel: {
        ...this.state.rowModesModel,
        [id]: { mode: GridRowModes.Edit },
      },
    });
  };

  handleSaveClick = (id: any) => {
    this.setState({
      rowModesModel: {
        ...this.state.rowModesModel,
        [id]: { mode: GridRowModes.View },
      },
    });
  };

  handleCancelClick = (id: any) => {
    this.setState({
      rowModesModel: {
        ...this.state.rowModesModel,
        [id]: { mode: GridRowModes.View, ignoreModifications: true },
      },
    });
  };

  processRowUpdate = (newRow: GridRowModel) => {
    const newId = this.getRowId(newRow);

    const rows = (this.props.rows || []).map((r: any) =>
      String(this.getRowId(r)) === String(newId)
        ? {
            ...r,
            ...newRow,
            id: r.id || newRow.id || newId,
            _state: (r.rid || r.taskid) ? 'dirty' : 'new',
          }
        : r
    );

    this.props.onChange(rows);
    return rows.find((r: any) => String(this.getRowId(r)) === String(newId)) || newRow;
  };

  saveAll = async () => {
    this.setState({ saving: true });

    try {
      await this.props.onSubmit(this.props.rows || []);
    } finally {
      this.setState({ saving: false });
    }
  };

  gridActions = (params: any) => {
    const isEditing = this.state.rowModesModel[params.id]?.mode === GridRowModes.Edit;

    if (isEditing) {
      return [
        <GridActionsCellItem
          key="save"
          icon={<SaveIcon />}
          label="Save"
          onClick={() => this.handleSaveClick(params.id)}
        />,
        <GridActionsCellItem
          key="cancel"
          icon={<CancelIcon />}
          label="Cancel"
          onClick={() => this.handleCancelClick(params.id)}
        />,
      ];
    }

    return [
      <GridActionsCellItem
        key="edit"
        icon={<EditIcon />}
        label="Edit"
        onClick={() => this.handleEditClick(params.id)}
      />,
      <GridActionsCellItem
        key="delete"
        icon={<DeleteIcon />}
        label="Delete"
        onClick={() => this.deleteRow(params.id)}
      />,
    ];
  };

  getColumns = (): GridColDef[] => [
    { field: 'code', headerName: 'Task Code', width: 150, editable: true },
    { field: 'name', headerName: 'Task Name', flex: 1, minWidth: 240, editable: true },
    { field: 'weight', headerName: 'Weight', width: 110, type: 'number', editable: true },
    { field: 'status', headerName: 'Status', width: 90 },
    { field: 'description', headerName: 'Description', flex: 1, minWidth: 260, editable: true },
    { field: 'actions', type: 'actions', width: 100, getActions: this.gridActions },
  ];

  render() {
    const { rows, loading, selectedTaskId } = this.props;

    return (
      <FDataGrid
        title="Tasks"
        subtitle="Select a task to manage its budget lines."
        rows={rows || []}
        columns={this.getColumns()}
        loading={loading || this.state.saving}
        height={360}
        addRow={this.addRow}
        saveAll={this.saveAll}
        showQuickFilter
        toolbarSx={{ backgroundColor: '#eaf2fb' }}
        getRowId={this.getRowId}
        editMode="row"
        rowModesModel={this.state.rowModesModel}
        onRowModesModelChange={(model: GridRowModesModel) => this.setState({ rowModesModel: model })}
        processRowUpdate={this.processRowUpdate}
        onProcessRowUpdateError={(err: any) => console.error(err)}
        onRowClick={(params: any) => this.props.onTaskSelect(params.row)}
        getRowClassName={(params: any) =>
          String(this.getRowId(params.row)) === String(selectedTaskId) ? 'selected-task-row' : ''
        }
        sx={{
          '& .selected-task-row': {
            bgcolor: '#e3f2fd',
          },
          '& .MuiDataGrid-row': {
            cursor: 'pointer',
          },
        }}
      />
    );
  }
}

export default TaskGridPanel;
